import { useState, useEffect } from 'react'
import { X, Upload, Plus, Trash2, CheckCircle, XCircle, Clock, Receipt, ChevronLeft, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'
import { expenseApi, EXPENSE_CATEGORY_LABELS, EXPENSE_STATUS_LABELS, type Expense, type ExpenseCategory, type ExpenseStatus } from '../../api/expenses'
import { Button } from '@/components/ui/button'

interface DriverExpensePanelProps {
  driverId: string
  driverName: string
  isOpen: boolean
  onClose: () => void
}

const STATUS_COLORS: Record<ExpenseStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

const STATUS_ICONS: Record<ExpenseStatus, React.ReactNode> = {
  pending: <Clock className="h-3 w-3" />,
  approved: <CheckCircle className="h-3 w-3" />,
  rejected: <XCircle className="h-3 w-3" />,
}

export default function DriverExpensePanel({ driverId, driverName, isOpen, onClose }: DriverExpensePanelProps) {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const limit = 10
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  // Form state
  const [category, setCategory] = useState<ExpenseCategory>('fuel')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [expenseDate, setExpenseDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [receiptFile, setReceiptFile] = useState<File | null>(null)

  const fetchExpenses = async () => {
    setLoading(true)
    try {
      const data = await expenseApi.getAllExpenses({ page, limit, driver_id: driverId })
      setExpenses(data.items)
      setTotal(data.total)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) fetchExpenses()
  }, [isOpen, page, driverId])

  const resetForm = () => {
    setCategory('fuel')
    setAmount('')
    setDescription('')
    setExpenseDate(format(new Date(), 'yyyy-MM-dd'))
    setReceiptFile(null)
    setImagePreview(null)
    setFormError('')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setReceiptFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setFormError('請輸入有效金額')
      return
    }
    setSubmitting(true)
    setFormError('')
    try {
      await expenseApi.createExpense({
        category,
        amount: parseFloat(amount),
        expense_date: new Date(expenseDate).toISOString(),
        description: description || undefined,
        receipt_image: receiptFile || undefined,
      })
      setShowForm(false)
      resetForm()
      setPage(1)
      fetchExpenses()
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || '新增失敗，請再試一次')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateStatus = async (id: string, status: ExpenseStatus) => {
    try {
      await expenseApi.updateExpense(id, { status })
      fetchExpenses()
    } catch {
      // ignore
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('確定刪除此支出記錄？')) return
    try {
      await expenseApi.deleteExpense(id)
      fetchExpenses()
    } catch (err: any) {
      alert(err?.response?.data?.detail || '刪除失敗')
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-gray-900 z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-500" />
              司機支出
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{driverName}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { resetForm(); setShowForm(true) }}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-3 py-1.5 h-auto"
            >
              <Plus className="h-4 w-4 mr-1" /> 新增支出
            </Button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* New Expense Form */}
        {showForm && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-gray-800 p-4 space-y-3">
            <h3 className="font-semibold text-gray-800 dark:text-white text-sm">新增支出記錄</h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">類別</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                >
                  {Object.entries(EXPENSE_CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">金額 (元)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">支出日期</label>
              <input
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">備注（選填）</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="例：中油加油站、南港輪胎更換..."
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">上傳發票/收據（選填）</label>
              <label className="flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-orange-400 px-4 py-3 transition-colors">
                <Upload className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">{receiptFile ? receiptFile.name : '點擊選擇圖片'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </label>
              {imagePreview && (
                <img src={imagePreview} alt="preview" className="mt-2 h-20 w-auto rounded-lg object-cover border border-gray-200" />
              )}
            </div>

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 text-sm" onClick={() => { setShowForm(false); resetForm() }}>取消</Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? '提交中...' : '確認新增'}
              </Button>
            </div>
          </div>
        )}

        {/* Expense List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">載入中...</div>
          ) : expenses.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm text-gray-400">尚無支出記錄</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {expenses.map((expense) => (
                <div key={expense.id} className="px-5 py-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 dark:text-white text-sm">
                          {EXPENSE_CATEGORY_LABELS[expense.category]}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[expense.status]}`}>
                          {STATUS_ICONS[expense.status]}
                          {EXPENSE_STATUS_LABELS[expense.status]}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-orange-600 dark:text-orange-400 mt-0.5">
                        NT$ {Number(expense.amount).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {format(new Date(expense.expense_date), 'yyyy/MM/dd')}
                        {expense.description && <span className="ml-2">· {expense.description}</span>}
                      </div>
                      {expense.reviewer_note && (
                        <div className="mt-1 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                          審核備注：{expense.reviewer_note}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {expense.receipt_image_path && (
                        <button
                          onClick={() => setLightboxSrc(expense.receipt_image_path!)}
                          className="cursor-pointer"
                        >
                          <img
                            src={expense.receipt_image_path}
                            alt="receipt"
                            className="h-12 w-12 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity"
                          />
                        </button>
                      )}
                      {expense.status === 'pending' && (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={() => handleUpdateStatus(expense.id, 'approved')}
                            className="p-1 rounded text-green-600 hover:bg-green-50 cursor-pointer transition-colors"
                            title="通過"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(expense.id, 'rejected')}
                            className="p-1 rounded text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                            title="駁回"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(expense.id)}
                            className="p-1 rounded text-gray-400 hover:bg-gray-100 cursor-pointer transition-colors"
                            title="刪除"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-500">共 {total} 筆</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxSrc(null)}
        >
          <img src={lightboxSrc} alt="receipt full" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}
    </>
  )
}
